import { useRef, useEffect, useCallback } from 'react';
import { SdkErrorResponse } from 'src/utilities/notifications';
import { IErrorResponse } from 'src/utilities/shared-types';

export type PollingError = IErrorResponse | SdkErrorResponse;

export interface UsePollingOptions {
  pollingIntervalMs: number;
  maxAttempts: number;
  onError?: (error: PollingError) => void;
}

export interface UsePollingReturn<T> {
  startPolling: () => Promise<T | undefined>;
  stopPolling: () => void;
}

export function usePolling<T>(
  fetchCallback: () => Promise<T>,
  shouldContinuePolling: (response: T) => boolean,
  options: UsePollingOptions
): UsePollingReturn<T> {
  const { pollingIntervalMs, maxAttempts, onError } = options;

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const retriesRef = useRef(0);
  const isMountedRef = useRef(true);

  const clearPolling = useCallback((): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return (): void => {
      isMountedRef.current = false;
      clearPolling();
    };
  }, [clearPolling]);

  const handlePollError = useCallback(
    (error: PollingError, isInitialCall: boolean): boolean => {
      clearPolling();
      if (!isInitialCall && onError && isMountedRef.current) {
        onError(error);
      }
      return isInitialCall;
    },
    [clearPolling, onError]
  );

  const executePoll = useCallback(
    async (isInitialCall = false): Promise<T | undefined> => {
      const scheduleNextPollIfNeeded = (result: T): void => {
        if (shouldContinuePolling(result) && retriesRef.current < maxAttempts) {
          retriesRef.current += 1;
          timeoutRef.current = setTimeout((): void => {
            executePoll(false);
          }, pollingIntervalMs);
        } else {
          clearPolling();
        }
      };

      try {
        if (!isMountedRef.current) return undefined;
        const result = await fetchCallback();
        if (!isMountedRef.current) return undefined;
        scheduleNextPollIfNeeded(result);
        return result;
      } catch (error) {
        if (handlePollError(error as PollingError, isInitialCall)) throw error;
        return undefined;
      }
    },
    [
      fetchCallback,
      shouldContinuePolling,
      maxAttempts,
      pollingIntervalMs,
      clearPolling,
      handlePollError,
    ]
  );

  const startPolling = useCallback(async (): Promise<T | undefined> => {
    clearPolling();
    retriesRef.current = 1;
    return executePoll(true);
  }, [clearPolling, executePoll]);

  return { startPolling, stopPolling: clearPolling };
}
